import { createContext, useContext } from 'react';
import type { Answer, Config, Content, ContentStep, Step } from './types.ts';
import type { AnswerMap } from './answers.ts';
import type { Projection } from './rules/projection.ts';
import type { Archetype } from './types.ts';
import type { PlanValues } from './plans.ts';
import type { CtaState } from '../shell/Shell.tsx';
import type { TrackedEvent } from './track.ts';

export interface FunnelCtx {
  config: Config;
  /** shared block with every {{…}} already resolved */
  shared: Content['shared'];
  answers: AnswerMap;
  setAnswer: (key: string, value: Answer) => void;
  stepIndex: number;
  next: () => void;
  goTo: (id: string) => void;
  back: () => void;
  setCta: (state: CtaState) => void;
  toast: (message: string, ms?: number) => void;
  projection: Projection;
  archetype: Archetype;
  plans: PlanValues[];
  selectedPlan: PlanValues;
  selectPlan: (id: string) => void;
  /** template render + [PH] strip, with the live roots */
  render: <T>(node: T) => T;
  /** same as render, but {{plan.*}} resolves against the plan given (per-card values) */
  renderPlan: (text: string, plan: PlanValues) => string;
  event: (name: string, params?: Record<string, unknown>) => TrackedEvent;
  /** ms the current step has been on screen */
  sinceStepView: () => number;
  leadEmail: string | null;
  submitLead: (email: string) => Promise<void>;
}

export interface StepProps {
  step: Step;
  content: ContentStep;
  ctx: FunnelCtx;
}

export const FunnelContext = createContext<FunnelCtx | null>(null);

export function useFunnel(): FunnelCtx {
  const ctx = useContext(FunnelContext);
  if (!ctx) throw new Error('useFunnel outside the funnel tree');
  return ctx;
}
